import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import FadeIn from 'react-fade-in/lib/FadeIn';
import { useTranslation } from 'react-i18next';
import { BsChevronDown } from 'react-icons/bs';
import { createUseStyles } from 'react-jss';
import { Feature } from '../../hooks/use-feature';
import { layoutDefaults } from '../../utils/layout-defaults';
import {
  computePercent,
  isPayable,
  parseOrderDescription,
  parseTip,
  parseToDistinctOrders,
} from '../../utils/manager/order-manager';
import { parseValue } from '../../utils/manager/price-manager';

function PaymentDetails({
  style,
  backgroundColor,
  payment,
  spot,
  menuInfo,
  feature,
}) {
  const { t } = useTranslation();
  const classes = useStyles({ backgroundColor });
  const [showDetail, setShowDetail] = useState(false);
  const { isFeatureEnabled } = feature;
  const symbol = menuInfo.i18n?.currency?.symbol;
  const suggestion = menuInfo.on_site?.tip?.suggestion ?? 0;

  const distinctOrders = useMemo(
    () => parseToDistinctOrders(payment.orders),
    [payment.orders]
  );

  const totalFormmated = useMemo(() => {
    let { value } = payment;

    if (value == null) {
      value = payment.orders
        .filter((order) => isPayable(order))
        .reduce((acc, order) => acc + order.value, 0);
    }

    const result = parseValue(
      value + (payment.tip ?? 0) + (payment.remaining_fee?.amount ?? 0)
    );
    return result;
  }, [payment.value, payment.tip]);

  const defaultTip = useMemo(
    () =>
      parseTip(
        payment.orders,
        isFeatureEnabled(Feature.ON_SITE_TIP),
        suggestion,
        false
      ),
    [payment.orders]
  );

  const remainingAmountAsterisk = useMemo(() => {
    if (isFeatureEnabled(Feature.ON_SITE_TIP) && !spot?.type?.addressable) {
      return '**';
    }

    return '*';

    // TODO: xxx
  }, [feature, spot]);

  const tipPercent = useMemo(
    () => computePercent(payment.tip, defaultTip, suggestion),
    [payment.value, payment.tip, defaultTip]
  );

  const toggleShowDetail = () => {
    setShowDetail(!showDetail);
  };

  if (!showDetail) {
    return (
      <div className={style}>
        <FadeIn>
          <div className={classes.total} onClick={toggleShowDetail} aria-hidden>
            <span className={classes.totalLabel}>{t('payment.value')}</span>
            <span className={classes.totalValue}>
              {symbol} {totalFormmated}
              {payment.remaining_fee?.amount > 0 && '*'}
            </span>
            <div className={classes.downButton}>
              <BsChevronDown />
            </div>
          </div>
        </FadeIn>
        {/* <div className={classes.showOrdersContainer}>
          <Button
            variant="link"
            onClick={toggleShowDetail}
            className={classes.link}
          >
            {t('payment.details.show')}
          </Button>
        </div> */}
        {payment.remaining_fee?.amount > 0 && (
          <div className={classes.remainingFeeMessage}>
            *
            {t('payment.details.remaining-fee', {
              symbol,
              amount: parseValue(payment.remaining_fee?.amount),
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classNames(style, classes.ordersContainer)}>
      <div className={classes.ordersTitle}>
        {spot?.prepaid ? t('items') : t('orders')}{' '}
      </div>
      <FadeIn className={classes.orders}>
        {distinctOrders.map((order) => (
          <div key={order.id} className={classes.order}>
            <span className={classes.orderCount}>{order.count} x </span>
            <span className={classes.orderDescription}>
              {parseOrderDescription(order)}
              {isFeatureEnabled(Feature.ON_SITE_TIP) &&
                !order.item?.non_tip &&
                tipPercent > 0 && <span>*</span>}
            </span>
          </div>
        ))}
        {isFeatureEnabled(Feature.ON_SITE_TIP) && (
          <div className={classes.order}>
            {tipPercent > 0 && (
              <>
                *
                <span className={classes.tip}>
                  {t('payment.choose-pay-tip_1')}
                  <b>{tipPercent}%</b>
                  {t('payment.choose-pay-tip_2')}
                </span>
              </>
            )}
          </div>
        )}
      </FadeIn>
      <FadeIn>
        <div
          className={classNames(classes.total, classes.dashed)}
          onClick={toggleShowDetail}
          aria-hidden
        >
          <span className={classes.totalLabel}>{t('payment.value')}</span>
          <span className={classes.totalValue}>
            {symbol} {totalFormmated}
            {payment.remaining_fee?.amount > 0 && remainingAmountAsterisk}
          </span>
        </div>
        <div className={classes.transaction}>{payment.id}</div>
      </FadeIn>
      {payment.remaining_fee?.amount > 0 && (
        <div className={classes.remainingFeeMessage}>
          {remainingAmountAsterisk}
          {t('payment.details.remaining-fee', {
            symbol,
            amount: parseValue(payment.remaining_fee?.amount),
          })}
        </div>
      )}
      <div className={classes.showOrdersContainer}>
        <Button
          variant="link"
          onClick={toggleShowDetail}
          className={classes.link}
        >
          {t('payment.details.hide')}
        </Button>
      </div>
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    // border: '1px solid white',
  },
  // linkContainer: {
  //   flex: 1,
  //   alignItems: 'flex-end',
  //   justifyContent: 'flex-end',
  //   border: '1px solid blue',
  // },
  link: {
    cursor: 'pointer',
    alignSelf: 'flex-end',
    fontSize: layoutDefaults.fontSize,
    color: layoutDefaults.linkColor,
    padding: 0,
    // paddingRight: 0,
    // marginTop: layoutDefaults.verticalMargin,
    // border: '1px solid blue',
  },
  downButton: {
    position: 'absolute',
    color: layoutDefaults.buttonText,
    right: layoutDefaults.bigHorizontalMargin,
    bottom: layoutDefaults.verticalMargin,
  },
  ordersContainer: {
    backgroundColor: ({ backgroundColor }) =>
      backgroundColor ?? layoutDefaults.cardColor,
    // marginTop: layoutDefaults.verticalMargin,
    paddingTop: layoutDefaults.verticalMargin,
    paddingBottom: layoutDefaults.verticalMargin + 4,
    paddingLeft: layoutDefaults.bigHorizontalMargin,
    paddingRight: layoutDefaults.bigHorizontalMargin,
    // border: "1px dashed #bbb",
    // borderRadius: layoutDefaults.radius,
  },
  ordersTitle: {
    textAlign: 'left',
    textTransform: 'uppercase',
    // fontWeight: "bolder",
    fontSize: layoutDefaults.smallerFontSize,
    opacity: layoutDefaults.smallOpacity,
    marginTop: layoutDefaults.smallVerticalMargin,
    marginBottom: layoutDefaults.smallVerticalMargin,
  },
  orders: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontSize: layoutDefaults.fontSize,
    // opacity: layoutDefaults.opacity,
    /* text-align: center; */
    // marginTop: layoutDefaults.smallVerticalMargin,
    // marginBottom: -layoutDefaults.smallVerticalMargin,
    // color: "#999",
    // border: '1px solid red',
  },
  showOrdersContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    marginTop: layoutDefaults.verticalMargin,
  },
  order: {
    marginTop: layoutDefaults.smallVerticalMargin,
    marginBottom: layoutDefaults.smallVerticalMargin,
    textAlign: 'left',
    // fontSize: '.84em',
    // border: '1px solid blue',
  },
  tip: {
    fontSize: layoutDefaults.smallerFontSize,
    fontStyle: 'italic',
    // opacity: layoutDefaults.opacity,
  },
  orderCount: {
    fontWeight: 'bold',

    display: 'inline-block',
    alignSelf: 'center',
    whiteSpace: 'nowrap',
  },
  orderDescription: {
    flex: 1,
    marginLeft: layoutDefaults.smallerHorizontalMargin,
    alignSelf: 'center',
    textAlign: 'left',
  },
  transaction: {
    opacity: layoutDefaults.smallOpacity,
    // textAlign: 'left',
    fontSize: layoutDefaults.smallestFontSize,
    textTransform: 'uppercase',
    paddingTop: layoutDefaults.smallerVerticalMargin,
    paddingBottom: layoutDefaults.smallerVerticalMargin,
    // marginBottom: layoutDefaults.smallVerticalMargin,
    // border: "1px solid red",
  },
  total: {
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    // borderRadius: layoutDefaults.radius,
    backgroundColor: ({ backgroundColor }) =>
      backgroundColor ?? layoutDefaults.cardColor,
    // backgroundColor: "#efefef",
    // marginTop: layoutDefaults.smallVerticalMargin,
    padding: layoutDefaults.verticalMargin,
  },
  dashed: {
    border: `1px dashed ${layoutDefaults.buttonBorder}`,
  },
  totalLabel: {
    fontSize: layoutDefaults.smallestFontSize,
    // opacity: layoutDefaults.opacity,
    /* font-stretch: expanded; */
    textTransform: 'uppercase',
  },
  totalValue: {
    // fontWeight: "normal",
    // fontSize: layoutDefaults.biggestFontSize,
    // fontWeight: 'bold',
    fontSize: layoutDefaults.bigFontSize,
    // fontWeight: 'bold',
  },
  remainingFeeMessage: {
    // textTransform: 'uppercase',
    // textAlign: 'justify',
    // color: layoutDefaults.primaryColor,
    textAlign: 'left',
    fontSize: layoutDefaults.fontSize,
    opacity: layoutDefaults.opacity,

    marginTop: layoutDefaults.bigVerticalMargin,
    // marginLeft: layoutDefaults.horizontalMargin,
  },
  // totalValueSmall: {
  //   fontSize: layoutDefaults.bigFontSize,
  //   fontWeight: 'bold',
  // },
});

export { PaymentDetails };
